import React, { useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Grid, makeStyles, CircularProgress, Box, Theme } from '@material-ui/core';
import { RCNumericMetricType, NumericMetricInfo, RobotDefinition } from '@moderepo/mode-apis';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import { Agent, TagMetricInfo } from '@moderepo/mode-internal-apis';
import { capitalizeAll, DEFAULT_TSDB_SMART_MODULE_ID, getLanguageByCode, getRobotNumericMetricInfoObjects, getRobotTimeSeriesCollectionId,
    mergeMetricDataToMultiMetricDataPoints, MultiSeriesDataPoint, XYChart, TimeSeriesChartPanel, capitalizeFirst, ChartSettings,
    getRobotDefinitionByRobotClass, getRobotTagMetricInfoObjects, convertCollectionDataToMetricsDataPoints, Time, roundValue, GenericMessageBox }
    from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchRobotById, fetchRobotCatalog, fetchRobotCloudProjectSettings, rcDataContext, selectRobotById, selectRobotCatalog,
    selectRobotCloudProjectSettings } from '@moderepo/mode-data-state-robot-cloud';
import { fetchTimeSeriesCollectionInfo, fetchTimeSeriesCollectionRawData, userAppDataContext, selectTimeSeriesCollectionInfo,
    selectTimeSeriesCollectionRawData } from '@moderepo/mode-data-state-user-app';
import * as Utils from '../../utils/utils';
import * as Constants from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';
import { GanttChart } from '../time-series/GanttChart';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { getNumericMetricsRange } from './RCUtils';
import { LazyComp } from '..';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            '& .numeric-metric-chart': {
                height: 300,
            },
        },

        title: {
            fontWeight: 500,
            fontSize  : 'large',
            color     : theme.palette.text.primary,
            textAlign : 'center',
        },

        reloadDataMessage: {
            fontWeight: 500,
            color     : theme.palette.success.main,
            textAlign : 'center',
            paddingTop: theme.spacing(0.5),
        },

        chart: {
            width : '100%',
            height: '100%',
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },
    };
}, { name: 'RobotCloudDeviceMetricContainer' });


// How often to reload data
const RELOAD_DATA_FREQ_IN_SECONDS = 60;

/**
 * Sort the list of numeric metrics by the order they were defined in the robot definition
 * @param numericMetrics
 * @param robotCatalog
 * @param robotClass
 * @returns
 */
export const sortNumericMetricsByMetricsDefinition = (
    numericMetrics: readonly NumericMetricInfo[], robotCatalog: readonly RobotDefinition[] | undefined, robotClass: string,
): readonly NumericMetricInfo[] => {

    // Get the robotDef from the catalog the device belonged to. NOTE: this can be undefined if this robot/device
    // does not exist in the catalog
    const robotDef: RobotDefinition | undefined = getRobotDefinitionByRobotClass(robotCatalog, robotClass);

    if (robotDef) {
        /**
         * Create a map of the metric's valueName => metric's index in robot definition. Lets say the metrics are defined in this order
         * ["pressure", "humidity", "temperature"] therefore this will produce something like this
         *      metricsIndices = {
         *          temperature: 2,
         *          pressure: 0,
         *          humidity: 1
         *      }
         */
        const metricsIndices = robotDef.numericMetrics.reduce((result, metric, index): {
            [valueName: string]: number
        } => {
            return {
                ...result,
                [metric.name]: index,
            };
        }, {});

        // Now sort the metrics by comparing their indices
        return [...numericMetrics].sort((metric1: NumericMetricInfo, metric2: NumericMetricInfo) => {
            const index1 = metricsIndices[metric1.name] || 0;
            const index2 = metricsIndices[metric2.name] || 0;
            return index1 - index2;
        });
    }

    // If there is no robotDefinition, return the original metrics
    return numericMetrics;
};



/**
 * Sort the list of numeric metrics by the order they were defined in the robot definition
 * @param tagMetrics
 * @param robotCatalog
 * @param robotClass
 * @returns
 */
export const sortTagMetricsByMetricsDefinition = (
    tagMetrics: readonly TagMetricInfo[], robotCatalog: readonly RobotDefinition[] | undefined, robotClass: string,
): readonly TagMetricInfo[] => {

    // Get the robotDef from the catalog the device belonged to. NOTE: this can be undefined if this robot/device
    // does not exist in the catalog
    const robotDef: RobotDefinition | undefined = getRobotDefinitionByRobotClass(robotCatalog, robotClass);

    if (robotDef) {
        /**
         * Create a map of the metric's valueName => metric's index in robot definition. Lets say the metrics are defined in this order
         * ["pressure", "humidity", "temperature"] therefore this will produce something like this
         *      metricsIndices = {
         *          temperature: 2,
         *          pressure: 0,
         *          humidity: 1
         *      }
         */
        const metricsIndices = robotDef.tagMetrics?.reduce((result, metric, index): {
            [valueName: string]: number
        } => {
            return {
                ...result,
                [metric.name]: index,
            };
        }, {}) || {};

        // Now sort the metrics by comparing their indices
        return [...tagMetrics].sort((metric1: TagMetricInfo, metric2: TagMetricInfo) => {
            const index1 = metricsIndices[metric1.name] || 0;
            const index2 = metricsIndices[metric2.name] || 0;
            return index1 - index2;
        });
    }

    // If there is no robotDefinition, return the original metrics
    return tagMetrics;
};


interface RCRobotMetricsDashboardContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly robotId: string;
}



/**
 * This is a DUMB component used for displaying health status of ONE device. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCRobotMetricsDashboardContainer = (props: RCRobotMetricsDashboardContainerProps) => {
    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = selectProjectById(dataState, props.projectId);
    const robot = selectRobotById(rcDataState, props.homeId, props.robotId);
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);
    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const [reloadDataCountdownTimer, setReloadDataCountdownTimer] = useState<number>(RELOAD_DATA_FREQ_IN_SECONDS);



    // Some metrics doesn't make sense to be displayed as chart e.g. location metrics so this is the filtered list of metrics
    // that can be shown as charts. We won't be showing charts for ALL possible metrics a device has.
    const [chartableMetrics, setChartableMetrics] = useState<{
        readonly numericMetrics: readonly(readonly NumericMetricInfo[])[] | undefined
        readonly tagMetrics: readonly (readonly TagMetricInfo[])[] | undefined
    }>();

    // The metric data
    const [multiMetricsDataPoints, setMultiMetricsDataPoints] = useState<{
        numericMetrics: readonly MultiSeriesDataPoint<number>[] | undefined,
        tagMetrics: readonly MultiSeriesDataPoint<string>[] | undefined,
    } | undefined>();


    const [dateRange, setDateRange] = useState<{
        readonly start: number;
        readonly end: number;
    }>({
        start: Date.now() - Time.DAY_IN_MS,
        end  : Date.now(),
    });


    const selectedLanguage = getLanguageByCode(
        Constants.SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || Constants.DEFAULT_LANGUAGE;
    const currentLanguage = loggedInAgent
        ? Utils.getPreferredLanguage(project, loggedInAgent, Constants.DEFAULT_LANGUAGE)
        : selectedLanguage;


    // This state is used for remembering the params we used for fetching data so that we can use the same params to select the data from data state
    const fetchDataParams = useRef<{
        readonly start: string;
        readonly numericMetricNames: readonly string[];
        readonly tagMetricNames: readonly string[];
    } | undefined>();


    /**
     * Get the timeSeries collection info from data state
     */
    const tsCollectionInfo = useMemo(() => {
        if (robot && robot.deviceId && rcProjectSettings) {
            const collectionId = `${getRobotTimeSeriesCollectionId(robot.robotClass, robot.id)}`;
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;
            return selectTimeSeriesCollectionInfo(appDataState, robot.homeId, tsdbSmartModuleId, collectionId);
        }

        return undefined;
    }, [appDataState, rcProjectSettings, robot]);


    /**
     * This use effect is used for fetching robot data on component load if robot data is not loaded
     */
    useEffect(() => {
        if (!robot) {
            rcDataDispatch(fetchRobotById(props.projectId, props.homeId, props.robotId));
        }
    }, [robot, props.homeId, props.robotId, props.projectId, rcDataDispatch]);



    /**
     * This use effect is used for fetching robot cloud project settings on component load
     */
    useEffect(() => {
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [rcProjectSettings, props.projectId, rcDataDispatch]);


    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, props.projectId, rcDataDispatch]);



    /**
     * This useEffect is used for fetching the collection info for the selected robot on component init. All it will do is
     * dispatch an action to fetch collection info, nothing else. There will be another useEffect that will fetch collection data
     * when the collectionInfo is loaded. When the collectionInfo is loaded, we will know what valueNames and tagNames the collection
     * has and then we will know what metric data we need to fetch.
     */
    useEffect(() => {
        if (robot && robot.deviceId && rcProjectSettings && !tsCollectionInfo) {
            const collectionId = `${getRobotTimeSeriesCollectionId(robot.robotClass, robot.id)}`;
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;

            appDataDispatch(
                fetchTimeSeriesCollectionInfo(robot.homeId, tsdbSmartModuleId, collectionId, true, {
                    homeId    : robot.homeId,
                    id        : collectionId,
                    moduleId  : tsdbSmartModuleId,
                    timeZone  : '',
                    valueNames: [],
                    tagNames  : [],
                }), true, true,
            ).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robot, rcProjectSettings, tsCollectionInfo, appDataDispatch]);


    /**
     * This will only dispatch an event to fetch collection data and that's all. There will be another useEffect that will wait for the
     * data to be loaded and do something with the data
     */
    const fetchCollectionData = useCallback(() => {
        if (rcProjectSettings && robot && tsCollectionInfo) {
            const collectionId = `${getRobotTimeSeriesCollectionId(robot.robotClass, robot.id)}`;
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;
            const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);


            // Get the list of valueNames and tagNames to fetch data for. The valueNames and tagNames from the tsCollectionInfo might
            // contain old valueNames that are no longer used therefore we need to compare them with the robotDefinition's numericMetrics'
            // and tagMetrics filter out valueNames and tagNames that are no longer defined in robot definition
            const valueNames = (robotDef ? tsCollectionInfo.valueNames.filter((valueName: string) => {
                return robotDef.numericMetrics.find((numericMetric: NumericMetricInfo) => {
                    return numericMetric.name === valueName;
                }) !== undefined;
            }) : tsCollectionInfo.valueNames) || [];

            // Get the list of Tags from the robot definition or use the collectionInfo.tagNames
            const tagNames = (robotDef && tsCollectionInfo.tagNames ? tsCollectionInfo.tagNames.filter((tagName: string) => {
                return robotDef.tagMetrics?.find((tagMetric: TagMetricInfo) => {
                    return tagMetric.name === tagName;
                }) !== undefined;
            }) : tsCollectionInfo.tagNames) || [];


            // if there is at least 1 metrics, dispatch an action to fetch collection data for those metrics
            if (valueNames.length > 0 || tagNames.length > 0) {

                // After the data is loaded, we will need to select it from the data state. To select it from the data state, we will need to specify
                // the params we use when we fetch the data. Therefore, we need to save these params so that we can use them to select data later.
                const startDateString = (new Date()).toISOString();
                fetchDataParams.current = {
                    start             : startDateString,
                    numericMetricNames: valueNames,
                    tagMetricNames    : tagNames,
                };

                appDataDispatch(fetchTimeSeriesCollectionRawData(
                    robot.homeId, tsdbSmartModuleId, collectionId, startDateString, -100, valueNames, tagNames,
                ), true, true).catch(() => {
                    // ignore error because dataDispatch already handled the error
                });
            }
        }
    }, [appDataDispatch, rcProjectSettings, robot, robotCatalog, tsCollectionInfo]);


    /**
     * This useEffect is used for fetching collection DATA 1 time initially when the component load.
     */
    useEffect(() => {
        if (rcProjectSettings && robot && tsCollectionInfo) {
            fetchCollectionData();
        }
    }, [fetchCollectionData, rcProjectSettings, robot, tsCollectionInfo]);


    /**
     * This useEffect is used for reloading data when the countdown timer hit 0. It will also reset the countdown timer afterward.
     */
    useEffect(() => {
        if (reloadDataCountdownTimer <= 0) {
            fetchCollectionData();
            // Reset the countdown
            setReloadDataCountdownTimer(RELOAD_DATA_FREQ_IN_SECONDS);
        }
    }, [fetchCollectionData, reloadDataCountdownTimer]);


    /**
     * This useEffect will handle updating the countdown timer every 1 second
     */
    useEffect(() => {
        // Create a timer to decrease the nextUpdateCountdown by 1 every second
        const timerId = window.setInterval(() => {
            setReloadDataCountdownTimer((currentValue) => {
                return Math.max(0, currentValue - 1);   // make sure the value doesn't go below 0
            });
        }, 1000);

        // Return a cleanup function that will clear the timerId when the component unload so that it doesn't continue to run
        // after the user leave this page
        return () => {
            window.clearInterval(timerId);
        };
    }, [fetchCollectionData]);



    /**
     * This useEffect will wait for collection data and setup the data to be rendered
     */
    useEffect(() => {
        if (rcProjectSettings && robot && fetchDataParams.current) {

            const collectionId = `${getRobotTimeSeriesCollectionId(robot.robotClass, robot.id)}`;
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;

            // Use the fetchDataParams we saved earlier to fetch the collection data
            const lastFetchDataParams = fetchDataParams.current;

            const collectionData = selectTimeSeriesCollectionRawData(
                appDataState, robot.homeId, tsdbSmartModuleId, collectionId, lastFetchDataParams.start, -100,
                lastFetchDataParams?.numericMetricNames, lastFetchDataParams?.tagMetricNames,
            );


            if (collectionData) {
                // We have fetch and select the data successfully, we can now clear the fetchDataParams
                fetchDataParams.current = undefined;

                if (collectionData.data && collectionData.data.length > 0) {
                    // Use the first and last data points from the collectionData to set the Date range
                    // NOTE: because we use -limit to fetch raw data, the data is in reverse, the latest data point first and the oldest data point
                    // last.
                    setDateRange({
                        start: Date.parse(collectionData.data[collectionData.data.length - 1][0] as string),      // the last data point's date
                        end  : Date.parse(collectionData.data[0][0] as string),                                   // the first data point's date
                    });


                    // Convert collection's data format from backend to a map of metricName => DataPoints array and separate them into
                    // 2 sets of data, numericMetrics and tagMetrics
                    const reformattedNewData = convertCollectionDataToMetricsDataPoints(
                        collectionData, lastFetchDataParams.numericMetricNames, lastFetchDataParams.tagMetricNames,
                    );

                    // Convert the reformattedNewData to MultiSeriesData which can be used by the charts
                    setMultiMetricsDataPoints({
                        numericMetrics: mergeMetricDataToMultiMetricDataPoints(reformattedNewData.numericMetrics),
                        tagMetrics    : mergeMetricDataToMultiMetricDataPoints(reformattedNewData.tagMetrics),
                    });


                    // For numeric metrics, we need to filter out metric values that we can't display as charts or doesn't make sense to display as
                    // charts e.g. location metrics
                    // TODO - Remove DOOR and RADAR type when we have better way to display these values.
                    const excludeTypes = [
                        RCNumericMetricType.LATITUDE, RCNumericMetricType.LONGITUDE, RCNumericMetricType.ALTITUDE, RCNumericMetricType.FLOOR,
                        RCNumericMetricType.CONNECTION, RCNumericMetricType.DOOR, RCNumericMetricType.RADAR,
                    ];

                    const numericMetrics = sortNumericMetricsByMetricsDefinition(
                        getRobotNumericMetricInfoObjects(robotCatalog, lastFetchDataParams.numericMetricNames, robot.robotClass),
                        robotCatalog, robot.robotClass,
                    );
                    const tagMetrics = sortTagMetricsByMetricsDefinition(
                        getRobotTagMetricInfoObjects(robotCatalog, lastFetchDataParams.tagMetricNames, robot.robotClass),
                        robotCatalog, robot.robotClass,
                    );

                    setChartableMetrics({
                        numericMetrics: numericMetrics.filter((numericMetric: NumericMetricInfo) => {
                            return !numericMetric.type || !excludeTypes.includes(numericMetric.type as RCNumericMetricType);
                        }).map((numericMetric: NumericMetricInfo): readonly NumericMetricInfo[] => {
                            return [numericMetric];
                        }),
                        tagMetrics: tagMetrics.map((tagMetric: TagMetricInfo): readonly TagMetricInfo[] => {
                            return [tagMetric];
                        }),
                    });
                } else {
                    setChartableMetrics({
                        numericMetrics: [],
                        tagMetrics    : [],
                    });
                }
            }
        }
    }, [appDataState, dataState, rcProjectSettings, robot, robotCatalog]);



    /**
     * Create the chart settings and use useMemo to cache it
     */
    const chartSettings = useMemo((): ChartSettings => {
        return {
            className: clsx(classes.chart),
            locale   : currentLanguage.locale2,
        };
    }, [classes.chart, currentLanguage.locale2]);


    const hasChart = chartableMetrics && ((chartableMetrics.numericMetrics && chartableMetrics.numericMetrics.length > 0)
        || (chartableMetrics.tagMetrics && chartableMetrics.tagMetrics.length > 0));

    const isFetchingData = !robot && !rcProjectSettings && !chartableMetrics;


    return (
        <>
            {project && robot && hasChart && (
                <Grid className={classes.root} container spacing={3}>
                    <Grid item xs={12}>
                        <div className={classes.title}>
                            {capitalizeFirst(trans('label.last_updated_on_date', {
                                date: getDateTimeAndElapsedTime((new Date(dateRange.end)).toISOString(), trans, true),
                            }))}
                        </div>
                        <div className={classes.reloadDataMessage}>
                            {capitalizeAll(trans('label.next_update_in_time', {
                                time: reloadDataCountdownTimer,
                            }))}
                        </div>
                    </Grid>
                    {(chartableMetrics?.numericMetrics || []).map((numericMetric: readonly NumericMetricInfo[]) => {

                        // Get the latest value from the multiMetricsDataPoints
                        const numericMetrics = multiMetricsDataPoints?.numericMetrics;
                        const currentValue = numericMetrics && numericMetrics.length > 0
                            ? numericMetrics[numericMetrics.length - 1].values[numericMetric[0].name]
                            : null;

                        const yAxesRange = getNumericMetricsRange(numericMetric);

                        return (
                            <Grid item xs={12} md={6} lg={4} xl={3} key={numericMetric[0].name}>
                                <TimeSeriesChartPanel
                                    className="numeric-metric-chart"
                                    title={numericMetric[0].displayName || numericMetric[0].name}
                                    icon="show_chart"
                                    dataCount={multiMetricsDataPoints?.numericMetrics?.length || 0}
                                    seriesCount={numericMetric.length}
                                    texts={{
                                        currentValueLabel: capitalizeAll(trans('label.latest_value')),
                                    }}
                                    currentValue={currentValue !== null
                                        ? trans('label.value_unit_display', { value: roundValue(currentValue, 3), unit: numericMetric[0].unit })
                                        : undefined}
                                    noDataMessageComp={(
                                        <GenericMessageBox
                                            className={classes.noDataMessage}
                                            title={trans('generic.no_data.title', {
                                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                            })}
                                        />
                                    )}
                                >
                                    <LazyComp>
                                        <XYChart
                                            {...chartSettings}
                                            showBullets={false}
                                            showYAxes={numericMetric.length <= 2}
                                            chartId={numericMetric[0].name}
                                            dataPoints={multiMetricsDataPoints?.numericMetrics || []}
                                            displayMetrics={numericMetric}
                                            yAxesRange={yAxesRange}
                                        />
                                    </LazyComp>
                                </TimeSeriesChartPanel>
                            </Grid>
                        );
                    })}

                    {(chartableMetrics?.tagMetrics || []).map((tagMetric: readonly TagMetricInfo[]) => {

                        // Get the latest value from the multiMetricsDataPoints
                        const tagMetrics = multiMetricsDataPoints?.tagMetrics;
                        const currentValue = tagMetrics && tagMetrics.length > 0
                            ? tagMetrics[tagMetrics.length - 1].values[tagMetric[0].name]
                            : null;

                        return (
                            <Grid item xs={12} md={6} lg={4} xl={3} key={tagMetric[0].name}>
                                <TimeSeriesChartPanel
                                    className="numeric-metric-chart"
                                    title={tagMetric[0].displayName || tagMetric[0].name}
                                    icon="show_chart"
                                    dataCount={multiMetricsDataPoints?.tagMetrics?.length || 0}
                                    seriesCount={tagMetric.length}
                                    texts={{
                                        currentValueLabel: capitalizeAll(trans('label.latest_value')),
                                    }}
                                    currentValue={currentValue ?? undefined}
                                    noDataMessageComp={(
                                        <GenericMessageBox
                                            className={classes.noDataMessage}
                                            title={trans('generic.no_data.title', {
                                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                            })}
                                        />
                                    )}
                                >
                                    <LazyComp>
                                        <GanttChart
                                            {...chartSettings}
                                            chartId={tagMetric[0].name}
                                            dataPoints={multiMetricsDataPoints?.tagMetrics || []}
                                            displayMetrics={tagMetric}
                                            dateRange={dateRange}
                                        />
                                    </LazyComp>
                                </TimeSeriesChartPanel>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {robot && rcProjectSettings && chartableMetrics && !hasChart && !isFetchingData && (
                <Box marginTop={3}>
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                </Box>
            )}

            {isFetchingData && (
                <CircularProgress />
            )}
        </>
    );
};
